import type { FC, ReactNode } from 'react'
import type { GalleryItem } from '@/types'

import Image from 'next/image'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { pad } from '@/lib/format'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'

interface LightboxProps {
    items: GalleryItem[]
    index: number | null
    onClose: () => void
    onNavigate: (index: number) => void
}

/** Full-screen photo viewer with keyboard support and focus kept inside. */
export const Lightbox: FC<LightboxProps> = ({ items, index, onClose, onNavigate }): ReactNode => {
    const dialogRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const openerRef = useRef<HTMLElement | null>(null)
    const { lock, unlock } = useSmoothScroll()

    const open = index !== null

    useEffect(() => {
        if (!open) return

        openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        lock()
        closeRef.current?.focus()

        return () => {
            unlock()
            openerRef.current?.focus({ preventScroll: true })
        }
    }, [open, lock, unlock])

    useEffect(() => {
        if (index === null) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.preventDefault()
                onClose()
            } else if (event.key === 'ArrowRight') {
                onNavigate((index + 1) % items.length)
            } else if (event.key === 'ArrowLeft') {
                onNavigate((index - 1 + items.length) % items.length)
            } else if (event.key === 'Tab' && dialogRef.current) {
                const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button'))
                const first = focusable[0]
                const last = focusable[focusable.length - 1]

                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault()
                    last.focus()
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault()
                    first.focus()
                }
            }
        }

        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [index, items.length, onClose, onNavigate])

    if (index === null) return null

    const item = items[index]

    return createPortal(
        <div
            ref={dialogRef}
            role='dialog'
            aria-modal='true'
            aria-label='Course gallery'
            className='fixed inset-0 z-[80] flex flex-col bg-pine/95 text-bone backdrop-blur-md animate-in fade-in duration-500'
        >
            <div className='flex items-center justify-between px-5 py-4 sm:px-8 lg:px-12'>
                <p className='font-mono text-[11px] uppercase tracking-[0.24em] text-bone/70'>
                    {pad(index + 1)} / {pad(items.length)}
                </p>

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    className='flex h-10 items-center gap-2 rounded-full border border-bone/25 pl-4 pr-3 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors hover:border-bone/60'
                >
                    Close
                    <X className='size-4' aria-hidden='true' />
                </button>
            </div>

            <div className='relative flex min-h-0 flex-1 items-center justify-center px-5 sm:px-20 lg:px-28'>
                <figure key={item.src + index} className='flex h-full w-full flex-col items-center justify-center animate-in fade-in zoom-in-95 duration-700'>
                    <div className='relative min-h-0 w-full flex-1'>
                        <Image
                            src={item.src}
                            alt={item.alt}
                            fill
                            sizes='100vw'
                            className='object-contain'
                        />
                    </div>

                    <figcaption className='mt-4 text-center font-serif text-lg italic text-bone/85 sm:text-xl'>
                        {item.caption}
                    </figcaption>
                </figure>

                <button
                    type='button'
                    onClick={() => onNavigate((index - 1 + items.length) % items.length)}
                    className='absolute left-2 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-bone/25 bg-pine/60 transition-colors hover:border-bone hover:bg-bone hover:text-charcoal sm:left-6 lg:left-10'
                >
                    <ArrowLeft className='size-5' aria-hidden='true' />
                    <span className='sr-only'>
                        Previous photograph
                    </span>
                </button>

                <button
                    type='button'
                    onClick={() => onNavigate((index + 1) % items.length)}
                    className='absolute right-2 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-bone/25 bg-pine/60 transition-colors hover:border-bone hover:bg-bone hover:text-charcoal sm:right-6 lg:right-10'
                >
                    <ArrowRight className='size-5' aria-hidden='true' />
                    <span className='sr-only'>
                        Next photograph
                    </span>
                </button>
            </div>

            <div className='h-8 shrink-0 sm:h-10' />
        </div>,
        document.body
    )
}
